import { View, ViewRight } from 'common/types/view';
import { isDerivedReport } from 'common/views/view_types';
import { createContext, useContext, useMemo } from 'react';

import {
  userCanCreateDerivedReports,
  userCanUpdateDerivedReport,
} from './StorySaveDropdown/derivedReportPermissions';
import { hasRights } from 'common/views/has_rights';

interface ReportContext {
  currentStory: View;
  defaultStory: View | null;
}
/**
 * Provides context for the current _view_ on a page. It'll basically be the value of
 * Environment.CORE_VIEW.
 *
 * The only time it's null is if DerivedReportContext is not set in your component hierarchy.
 */
export const DerivedReportContext = createContext<ReportContext | null>(null);

/**
 * Helper function for using DerivedReportContext. Doing:
 *
 * ```ts
 * useContext(DerivedReportContext)
 * ```
 * will give you a type that's nullable because we _have to set a default for it_.
 *
 * This helper hides that away and throws an exception if it's null.
 *
 * @returns value of the DerivedReportContext
 * @throws when DerivedReportContext, which means the context was never provided.
 */
function useDerivedReportContext() {
  const context = useContext(DerivedReportContext);
  if (!context) {
    throw new Error('DerivedReportContext should never be null. Did you provide it?');
  }

  return context;
}

/**
 * Gives you the current view and certain other related values with it.
 *
 * @returns
 * currentStory: The current story
 *
 * defaultStory: The default story, if accessible by the current user (nullable)
 *
 * canCreate: whether can create derived reports off of a default report
 *
 * canUpdate: whether can update an existing derived report
 *
 * canDelete: Where the current user can delete the current derived report
 */
export function useDerivedReport() {
  const { currentStory, defaultStory } = useDerivedReportContext();

  const isDerived = useMemo(() => isDerivedReport(currentStory), [currentStory]);

  const canUpdate = useMemo(
    () => isDerived && userCanUpdateDerivedReport(currentStory),
    [isDerived, currentStory],
  );
  const canCreate = useMemo(() => userCanCreateDerivedReports(), []);
  const canDelete = useMemo(
    () => isDerived && hasRights(currentStory, ViewRight.DeleteView),
    [isDerived, currentStory],
  );

  return { currentStory, defaultStory, canUpdate, canCreate, canDelete };
}
